import React from 'react';
import styled from 'styled-components';
import { Check, Lock, Users } from 'lucide-react';
import useEventAccess from 'hooks/useEventAccess';
import { EVENT_PERMISSIONS, joinLabels } from 'helpers/eventCollaboration';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

const ABILITIES = [
    { permission: EVENT_PERMISSIONS.EDIT, label: 'Editar o evento' },
    { permission: EVENT_PERMISSIONS.SALES_VIEW, label: 'Ver vendas, pedidos e valores' },
    { permission: EVENT_PERMISSIONS.PARTICIPANTS_VIEW, label: 'Ver os participantes' },
    { permission: EVENT_PERMISSIONS.CHECKIN_TICKETS, label: 'Validar ingressos' },
    { permission: EVENT_PERMISSIONS.CHECKIN_PRODUCTS, label: 'Validar produtos' },
];

/**
 * Aviso fixo no topo do evento em que a pessoa só colabora: de quem é o
 * evento e, item a item, o que o convite liberou e o que ficou restrito.
 * Não aparece em evento próprio.
 */
export default function CollaborationNotice() {
    const { isCollaboration, owner, grantedLabels, can } = useEventAccess();

    if (!isCollaboration) return null;

    const ownerName = owner?.name || 'outro produtor';

    return (
        <Notice role='status'>
            <Heading>
                <Users size={16} color={panelAccent(colors.secondaryPurple)} aria-hidden='true' />
                <strong>Evento em colaboração</strong>
            </Heading>

            <Summary>
                Este evento é de <strong>{ownerName}</strong>. Você tem acesso somente a{' '}
                {grantedLabels.length ? <strong>{joinLabels(grantedLabels)}</strong> : 'nada além de abrir o evento'}.
                {' '}O resto fica restrito: valores aparecem ocultos e as páginas sem permissão ficam bloqueadas.
            </Summary>

            <Abilities aria-label='O que você pode neste evento'>
                {ABILITIES.map(({ permission, label }) => {
                    const allowed = can(permission);

                    return (
                        <Ability key={permission} allowed={allowed}>
                            {allowed
                                ? <Check size={12} strokeWidth={3} aria-hidden='true' />
                                : <Lock size={12} aria-hidden='true' />}
                            {label}
                            <Hidden>{allowed ? ' (liberado)' : ' (restrito)'}</Hidden>
                        </Ability>
                    );
                })}
            </Abilities>
        </Notice>
    );
}

const Notice = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0 0 4px;
    padding: 12px 14px;
    border-radius: 10px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '12')};
    border: 1px solid ${panelAccentAlpha(colors.occasionalPurple, '30')};
    color: ${colors.nightDark};
    font-size: 13px;
    line-height: 1.45;
`;

const Heading = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Summary = styled.p`
    margin: 0;
`;

const Abilities = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 0;
    padding: 0;
    list-style: none;

    @media screen and (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const Ability = styled.li`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 50px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    white-space: nowrap;

    ${({ allowed }) => allowed
        ? `
            background: #DCFCE7;
            border: 1px solid #B7E5C9;
            color: #166534;
        `
        : `
            background: ${colors.neutral};
            border: 1px solid ${colors.neutralDark};
            color: ${colors.night};
        `}
`;

const Hidden = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
`;
